import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import { FormContext } from 'modules/workflows/components/Sidepanel/schemas/nodeSidepanelSchema'
import { createTaskFormSchema } from 'modules/workflows/components/Sidepanel/schemas/taskFormSchema'

export const createRequestDocumentHandlerSchema = (formContext: FormContext) => {
  const employeeSegmentSchema = z.union([
    z.object({
      name: z.enum(['anyone_in_team', 'team_leads'], {
        message: i18n.t('workflows.sidepanel.approval.validation.strategy_required'),
      }),
      input: z
        .object({
          team_id: z
            .number({
              message: i18n.t('workflows.sidepanel.approval.validation.team_required'),
            })
            .nullable(),
        })
        .refine(
          (data) => {
            return data.team_id && data.team_id > 0
          },
          {
            message: i18n.t('workflows.sidepanel.approval.validation.team_required'),
          }
        ),
    }),
    z.object({
      name: z.enum(['employee_manager', 'admins', 'resource_owner'], {
        message: i18n.t('workflows.sidepanel.approval.validation.strategy_required'),
      }),
      input: z.object({}),
    }),
    z.object({
      name: z.literal('job_catalog_role'),
      input: z.object({
        role_id: z.string(),
      }),
    }),
  ])

  return createTaskFormSchema(formContext).extend({
    nodeType: z.literal('task'),
    nodeData: z.object({
      handlerType: z.literal('request_document'),
      payload: z
        .object({
          title: z.string().min(1, {
            message: i18n.t('procurement.workflows.basic_task_handler.validation.title_required'),
          }),
          description: z.string().min(1, {
            message: i18n.t(
              'procurement.workflows.basic_task_handler.validation.description_required'
            ),
          }),
          assignee_ids: z.array(z.number()).optional(),
          assignee_segments: z.array(employeeSegmentSchema).optional(),
          fallback_assignee_ids: z.array(z.number()).optional(),
          fallback_assignee_segments: z.array(employeeSegmentSchema).optional(),
          due_on_value: z
            .number({
              message: i18n.t(
                'procurement.workflows.basic_task_handler.validation.due_date_min_value'
              ),
            })
            .min(1, {
              message: i18n.t(
                'procurement.workflows.basic_task_handler.validation.due_date_min_value'
              ),
            }),
          due_on_unit: z.enum(['hours', 'days', 'weeks', 'months']),
        })
        .refine(
          (data) => {
            const hasAssignees = !!data.assignee_ids?.length
            const hasSegments = !!data.assignee_segments?.length
            return hasAssignees || hasSegments
          },
          {
            message: i18n.t(
              'procurement.workflows.basic_task_handler.validation.assignee_required'
            ),
            path: ['assignee_ids'],
          }
        ),
    }),
  })
}

export type RequestDocumentHandlerSchema = z.infer<
  ReturnType<typeof createRequestDocumentHandlerSchema>
>
